import React, { useEffect, useRef } from 'react';
import { Heart, Clock, ArrowLeft, CheckCircle2, AlertCircle, CheckSquare, Square, BookOpen } from 'lucide-react'; 
import { useGameLogic } from './hooks/useGameLogic';
import GameModeSelect from './GameModeSelect';
import GameResult from './GameResult';
import { getChoseong } from '../../utils/helpers';

// ▼▼▼ 안정적인 MP3 BGM 목록 ▼▼▼
const BGM_PLAYLIST = [
    "https://upload.wikimedia.org/wikipedia/commons/transcoded/3/35/Erik_Satie_-_Gymnop%C3%A9die_No._1.ogg/Erik_Satie_-_Gymnop%C3%A9die_No._1.ogg.mp3",
    "https://upload.wikimedia.org/wikipedia/commons/transcoded/6/61/Ludwig_van_Beethoven_-_Sonata_No._14%2C_Op._27_No._2_%28Moonlight%29_-_I._Adagio_sostenuto.ogg/Ludwig_van_Beethoven_-_Sonata_No._14%2C_Op._27_No._2_%28Moonlight%29_-_I._Adagio_sostenuto.ogg.mp3",
    "https://upload.wikimedia.org/wikipedia/commons/transcoded/2/25/Debussy_-_Clair_de_Lune_-_Amanda_Setelik.ogg/Debussy_-_Clair_de_Lune_-_Amanda_Setelik.ogg.mp3",
    "https://upload.wikimedia.org/wikipedia/commons/transcoded/1/1b/Johann_Sebastian_Bach_-_Air_on_the_G_String.ogg/Johann_Sebastian_Bach_-_Air_on_the_G_String.ogg.mp3",
    "https://upload.wikimedia.org/wikipedia/commons/transcoded/e/ee/Frederic_Chopin_-_Nocturne_Eb_major_Op.9_No.2.ogg/Frederic_Chopin_-_Nocturne_Eb_major_Op.9_No.2.ogg.mp3"
];

// ▼▼▼ GamePlay 컴포넌트 ▼▼▼
function GamePlay({ 
    user, qIndex, totalQuestions, lives, maxLives, timer, gameModeType,
    currentQ, targetKeywordObj, feedback, mode, expertStep, inputValue, currentChoices, isCurrentBookmarked,
    selectedLaw, 
    onBack, onToggleBookmark, onInputChange, onSubmitExpert, onSubmitChoice, inputRef
}) {

    const fullText = currentQ?.text || "";
    const targetWordRaw = targetKeywordObj?.word || "";
    const targetWord = targetWordRaw.trim(); 

    const parts = fullText.split(targetWord);
    let textBefore = fullText;
    let textAfter = "";

    if (parts.length > 1) {
        textBefore = parts[0];
        textAfter = parts.slice(1).join(targetWord);
    }

    const formatTitle = () => {
        const lawName = currentQ.law || ""; 
        const articleText = currentQ.article || "관련 조문";
        
        let fullTitle = articleText;
        if (lawName && !articleText.startsWith(lawName)) {
            fullTitle = `${lawName} ${articleText}`;
        }

        let colorClass = "bg-indigo-600"; 

        if (fullTitle.includes("시행규칙") || fullTitle.includes("규칙") || fullTitle.includes("별표")) {
            colorClass = "bg-amber-600"; 
        } else if (fullTitle.includes("시행령") || fullTitle.includes("영 제") || fullTitle.includes("영 매") || fullTitle.includes("대통령령")) {
            colorClass = "bg-emerald-600"; 
        } else {
            if (selectedLaw === "손실보상" && !fullTitle.includes("법")) {
                colorClass = "bg-amber-600";
            }
        }

        return { title: fullTitle, color: colorClass };
    };

    const displayInfo = formatTitle();

    return (
        <div className="max-w-2xl mx-auto relative p-1">
            <div className="absolute inset-0 border-[6px] border-double border-amber-600/50 rounded-xl pointer-events-none"></div>
            
            <div className="relative z-10 p-2">
                <div className="flex justify-between items-center mb-6 bg-slate-800 p-4 rounded-xl shadow-md border border-slate-700">
                    {/* ▼▼▼ [핵심 수정] 오답통 모드가 아닐 때만 하트 표시 ▼▼▼ */}
                    {gameModeType !== 'odaptong_retry' && (
                        <div className="flex items-center space-x-1" key="hearts-container">
                          {/* maxLives가 없으면 기본 7개 */}
                          {Array.from({ length: maxLives || 7 }).map((_, index) => (
                            <Heart 
                              key={`heart-static-${index}`} 
                              className={`w-4 h-4 transition-all duration-300 ${
                                index < lives ? 'text-red-500 fill-red-500' : 'text-slate-600'
                              }`} 
                            />
                          ))}
                        </div>
                    )}
                    
                    <div className="flex items-center space-x-4">
                        {gameModeType === 'challenge' && (
                            <div className={`flex items-center font-mono font-bold text-xl ${timer <= 5 ? 'text-red-500 animate-pulse' : 'text-white'}`}>
                                <Clock className="w-5 h-5 mr-2" />
                                <span>{timer}</span>
                            </div>
                        )}
                        
                        <button onClick={onToggleBookmark} className={`flex items-center text-sm font-bold cursor-pointer bg-slate-900/50 px-3 py-1.5 rounded-lg border transition ${isCurrentBookmarked ? 'border-indigo-500 text-indigo-400' : 'border-slate-600 text-slate-400 hover:text-slate-200'}`}>
                            <span className="flex items-center mr-2">
                                {isCurrentBookmarked ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                            </span>
                            {/* [복구] 요청하신 대로 원본 문구 유지 */}
                            오답통으로 보내기
                        </button>

                        <span className="text-slate-400 font-medium text-sm ml-2"> Q-{qIndex + 1}/{totalQuestions}</span>
                    </div>
                </div>

                <div className="bg-slate-800 p-8 rounded-xl shadow-lg min-h-[300px] flex flex-col justify-between border border-slate-700 relative">
                    <div className="flex justify-between items-center w-full mb-6 pb-3 border-b border-slate-700/50">
                        <div className={`${displayInfo.color} text-white text-xs font-bold px-3 py-1.5 rounded flex items-center shadow-md transition-colors duration-300`}>
                            <BookOpen className="w-3 h-3 mr-1.5" />
                            {displayInfo.title}
                        </div>

                        <button onClick={onBack} className="flex items-center text-xs text-slate-400 hover:text-white border border-slate-600 hover:bg-slate-700 rounded px-3 py-1.5 transition">
                            <ArrowLeft className="w-3 h-3 mr-1" /> 나가기
                        </button>
                    </div>

                    <div className="flex-1 w-full flex flex-col items-center justify-center py-8 px-4 bg-[#111827] border border-slate-700 rounded-3xl shadow-2xl mb-6"> 
                        {/* 기존의 정렬 로직(w-fit, text-left, break-all)은 100% 유지 */}
                        <div className="w-fit max-w-full text-left text-base md:text-lg leading-relaxed font-medium text-slate-200 break-all">
                            <span>{textBefore}</span>
                            <span className={`mx-1 px-3 border-b-2 transition-colors duration-300 inline-block font-bold min-w-[60px] text-center align-bottom ${feedback === 'correct' ? 'border-green-500 text-green-400' : feedback === 'wrong' ? 'border-red-500 text-red-500' : 'border-indigo-500 text-indigo-400 bg-slate-900/50'}`}>
                                {feedback === 'correct' ? targetWord : (feedback === 'wrong' && (mode !== 'expert' || expertStep >= 2) ? targetWord : "???")}
                            </span>
                            <span>{textAfter}</span>
                        </div>
                    </div>
                    
                    <div className="mt-8">
                        {feedback === 'correct' && <div className="bg-green-900/30 border border-green-500/30 text-green-400 p-4 rounded-lg flex items-center justify-center animate-bounce"><CheckCircle2 className="w-6 h-6 mr-2" /> 정답입니다!</div>}
                        {feedback === 'wrong' && <div className="bg-red-900/30 border border-red-500/30 text-red-400 p-4 rounded-lg flex items-center justify-center animate-bounce"><AlertCircle className="w-6 h-6 mr-2" /> 오답입니다. {(mode !== 'expert' || expertStep >= 2) && <span className="text-sm ml-2 text-slate-300">정답: {targetKeywordObj.word}</span>}</div>}
                        {!feedback && (
                            <div className="animate-fadeIn">
                                {mode === 'expert' ? (
                                    <div className="space-y-4">
                                        {expertStep === 0 && (
                                            <form onSubmit={onSubmitExpert} className="flex">
                                                <input ref={inputRef} type="text" className="flex-1 bg-slate-900 border border-slate-600 rounded-l-lg p-4 text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="직접 입력하세요" value={inputValue} onChange={onInputChange} autoFocus />
                                                <button type="submit" className="bg-indigo-600 text-white px-6 rounded-r-lg font-bold text-base">확인</button>
                                            </form>
                                        )}
                                        {expertStep === 1 && (
                                            <div className="space-y-4">
                                                <div className="bg-indigo-900/30 border border-indigo-500/30 p-4 rounded-xl text-center"><p className="text-slate-400 text-xs mb-2 font-bold">💡 초성 힌트</p><span className="text-2xl tracking-[0.5em] font-mono text-indigo-300 ml-4">{getChoseong(targetKeywordObj.word)}</span></div>
                                                <form onSubmit={onSubmitExpert} className="flex">
                                                    <input ref={inputRef} type="text" className="flex-1 bg-slate-900 border border-indigo-500/50 rounded-l-lg p-4 text-white outline-none" placeholder="힌트를 보고 다시 입력" value={inputValue} onChange={onInputChange} autoFocus />
                                                    <button type="submit" className="bg-indigo-600 text-white px-6 rounded-r-lg font-bold text-base">확인</button>
                                                </form>
                                            </div>
                                        )}
                                        {expertStep === 2 && (
                                            <div className="grid grid-cols-2 gap-4">
                                                {currentChoices.map((choice, i) => (
                                                    <button key={i} onClick={() => onSubmitChoice(choice)} className="bg-slate-700 hover:bg-indigo-600 text-white p-4 rounded-xl transition font-bold text-base">
                                                        {choice}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-4">
                                        {currentChoices.map((choice, i) => (
                                            <button key={i} onClick={() => onSubmitChoice(choice)} className="bg-slate-700 hover:bg-indigo-600 text-white p-4 rounded-xl transition font-bold text-base">
                                                {choice}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ▼▼▼ Game 메인 ▼▼▼
export default function Game(props) {
    const { state, refs, data, actions } = useGameLogic({
        ...props,
        selectedLaw: props.selectedLaw
    });

    // ▼▼▼ BGM 제어 로직 (UI 변경 없이 백그라운드 재생) ▼▼▼
    const audioRef = useRef(null);

    useEffect(() => {
        if (state.gameState === 'playing') {
            // 이미 재생 중이면 리셋
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }

            const randomIndex = Math.floor(Math.random() * BGM_PLAYLIST.length);
            const selectedTrack = BGM_PLAYLIST[randomIndex];

            const audio = new Audio(selectedTrack);
            audio.loop = true;   
            audio.volume = 0.5; // 볼륨

            // 재생 시도
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    // [중요] 자동 재생이 막혔을 경우, 사용자가 화면을 클릭하면 그때 재생 시작
                    console.log("Auto-play blocked. Waiting for interaction.");
                    const resumeAudio = () => {
                        audio.play().catch(e => console.log("Still blocked", e));
                        document.removeEventListener('click', resumeAudio);
                        document.removeEventListener('keydown', resumeAudio);
                    };
                    document.addEventListener('click', resumeAudio);
                    document.addEventListener('keydown', resumeAudio);
                });
            }
            
            audioRef.current = audio;
        } else {
            // 게임 중이 아니면 멈춤
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        }

        // Cleanup
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, [state.gameState]); 

    const safeSelectedStage = (props.selectedStage && typeof props.selectedStage === 'object') 
        ? props.selectedStage.id 
        : props.selectedStage;

    const handleSafeNavigation = () => {
        if (props.gameModeType === 'challenge') {
            props.setView('home');
            return;
        }
        
        if (props.setSelectedStage) {
            props.setSelectedStage(safeSelectedStage);
        }
        props.setView('stage_select');
    };

    if (!state.questions.length && state.gameState === 'playing' && !state.feedback) {
        return <div className="text-center py-20 bg-slate-800 rounded-xl">준비 중...</div>;
    }

    if (state.gameState === 'mode-select') {
        return (
            <GameModeSelect 
                selectedLaw={props.selectedLaw}
                selectedStage={safeSelectedStage}
                gameModeType={props.gameModeType}
                setView={props.setView}
                setMode={actions.setMode}
                setGameState={actions.setGameState}
                setSelectedStage={props.setSelectedStage} 
            />
        );
    }

    if (state.gameState === 'game-over' || state.gameState === 'clear') {
        return (
            <GameResult 
                status={state.gameState}
                onRetry={actions.handleRetry}
                onHome={props.onHome}
                onStageSelect={handleSafeNavigation}
                onToExpert={actions.handleToExpert}
                handleNextStage={actions.handleNextStage}
                mode={state.mode}
                gameModeType={props.gameModeType}
                selectedLaw={props.selectedLaw}
                selectedStage={safeSelectedStage}
            />
        );
    }

    if (!data.currentQ || !data.targetKeywordObj) return <div className="text-center py-20 bg-slate-800 rounded-xl">로딩 중...</div>;

    return (
        <GamePlay 
            key={data.currentQ.id} 
            selectedLaw={props.selectedLaw}
            user={props.user}
            qIndex={state.qIndex}
            totalQuestions={state.questions.length}
            lives={state.lives}
            maxLives={7} 
            timer={state.timer}
            gameModeType={props.gameModeType}
            currentQ={data.currentQ}
            targetKeywordObj={data.targetKeywordObj}
            feedback={state.feedback}
            mode={state.mode}
            expertStep={state.expertStep}
            inputValue={state.inputValue}
            currentChoices={state.currentChoices}
            isCurrentBookmarked={state.isCurrentBookmarked}
            
            onBack={actions.handleBackStep}
            onToggleBookmark={actions.toggleBookmark}
            onInputChange={(e) => actions.setInputValue(e.target.value)}
            onSubmitExpert={actions.submitExpertInput}
            onSubmitChoice={actions.submitChoice}
            inputRef={refs.inputRef}
        />
    );
}